import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import {
  LOGICAL_OPERATORS,
  LogicalOperator,
  PartFileUnits,
} from '@frontend/api';
import {
  VStack,
  Button,
  Flex,
  HStack,
  IconButton,
  DragHandleIcon,
} from '@frontend/legacy-components';

import { ItemAndDropBorderContainer } from 'components/BetweenItemDropBorder';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import {
  addIdsToSignal,
  buildSignalFromCascaderValue,
} from '../utils/queryBuilding';
import { BetweenSignalDropBorder } from './BetweenSignalDropBorder';
import { type FormStateData } from './RulesDetailModal';
import {
  SignalComponentSimple,
  SignalComponentWithCaseWrapper,
} from './SignalComponent';
import { SignalSelect } from './SignalSelect';

const GAP = 2;

type SignalsContainerProps = {
  isReadOnly: boolean;
  logicalOperator: LogicalOperator;
  signalUnits: PartFileUnits;
};

export const SignalsContainer = ({
  isReadOnly,
  logicalOperator,
  signalUnits,
}: SignalsContainerProps) => {
  const { values, setFieldValue } = useFormikContext<FormStateData>();
  const signals = values.signals;

  const { configMap, cascaderOptions, valueTypeToAvailableUnits, isLoading } =
    useReqsReviewConfigContext();

  const handleCreateSignal = useCallback(
    (fullPathConfigArray: string[]) => {
      if (!configMap) return;

      const newSignal = buildSignalFromCascaderValue({
        nextCascaderValue: fullPathConfigArray,
        configMap,
        valueTypeToAvailableUnits,
      });

      if (!newSignal) {
        console.warn(
          `New signal not found for cascader value ${fullPathConfigArray}`,
        );
        return;
      }

      const newSignals = [...signals];
      newSignals.push(newSignal);
      setFieldValue('signals', newSignals);
    },
    [configMap, valueTypeToAvailableUnits, signals, setFieldValue],
  );

  const handleCreateEmptySignalFromOr = useCallback(() => {
    if (values.logicalOperator === LOGICAL_OPERATORS.AND) {
      setFieldValue('logicalOperator', LOGICAL_OPERATORS.OR);
    }

    const newSignal = {
      groups: [],
      logicalOperator: LOGICAL_OPERATORS.AND,
    };
    const newSignals = [...signals];
    newSignals.push(addIdsToSignal(newSignal));
    setFieldValue('signals', newSignals);
  }, [signals, setFieldValue, values.logicalOperator]);

  const handleDuplicateSignal = useCallback(
    (signalIndex: number) => {
      const signalToDuplicate = signals[signalIndex];
      if (!signalToDuplicate) return;

      // Create a deep copy of the signal with new IDs
      const newSignal = addIdsToSignal(
        JSON.parse(JSON.stringify(signalToDuplicate)),
      );

      const newSignals = [...signals];
      newSignals.splice(signalIndex + 1, 0, newSignal);
      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue],
  );

  const handleDeleteSignal = useCallback(
    (signalIndex: number) => {
      const newSignals = [...signals];
      newSignals.splice(signalIndex, 1);
      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue],
  );

  const handleReorderSignals = useCallback(
    (oldIndex: number, newIndex: number) => {
      const newSignals = [...signals];
      const [removed] = newSignals.splice(oldIndex, 1);
      const insertIndex = newIndex > oldIndex ? newIndex - 1 : newIndex;
      newSignals.splice(insertIndex, 0, removed);
      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue],
  );

  const handleReorderGroups = useCallback(
    (
      oldSignalIndex: number,
      oldGroupIndex: number,
      newSignalIndex: number,
      newGroupIndex: number,
    ) => {
      const newSignals = [...signals];

      // Remove the group from the old signal
      const [removedGroup] = newSignals[oldSignalIndex].groups.splice(
        oldGroupIndex,
        1,
      );

      // Add the group to the new signal at the new position
      const insertIndex =
        oldSignalIndex === newSignalIndex && newGroupIndex > oldGroupIndex
          ? newGroupIndex - 1
          : newGroupIndex;
      newSignals[newSignalIndex].groups.splice(insertIndex, 0, removedGroup);

      // If the old signal has no groups left, remove it
      if (
        newSignals[oldSignalIndex].groups.length === 0 &&
        oldSignalIndex !== newSignalIndex
      ) {
        newSignals.splice(oldSignalIndex, 1);
      }

      setFieldValue('signals', newSignals);
    },
    [signals, setFieldValue],
  );

  const signalContainerStyleProps = useMemo(() => {
    if (signals.length <= 1) {
      return {};
    } else {
      return {
        backgroundColor: 'page.plateLight',
        border: '1px solid',
        borderColor: 'separator.divider',
        borderRadius: 'lg',
        marginInline: -1,
        padding: 1,
      };
    }
  }, [signals.length]);

  if (isLoading) return null;

  return (
    <VStack gap={GAP} align="stretch" className="signals-container">
      <VStack
        gap={GAP}
        align="stretch"
        className="signals-selectors"
        {...signalContainerStyleProps}
      >
        {signals.length === 0 ? (
          <HStack>
            <IconButton size="md" isDisabled={true}>
              <DragHandleIcon />
            </IconButton>
            <SignalSelect
              isDisabled={isReadOnly}
              isClearable={false}
              placeholder="Add a signal"
              value={[]}
              options={cascaderOptions}
              onChange={handleCreateSignal}
              className="signal-select-add"
            />
          </HStack>
        ) : signals.length === 1 ? (
          <SignalComponentSimple
            signal={signals[0]}
            signalIndex={0}
            isReadOnly={isReadOnly}
            signalUnits={signalUnits}
            onReorderGroups={handleReorderGroups}
          />
        ) : (
          <VStack gap={GAP} align="stretch">
            {signals.map((signal, signalIndex) => (
              <ItemAndDropBorderContainer
                key={signal.id}
                display="flex"
                flexDirection="column"
                gap={GAP}
              >
                <BetweenSignalDropBorder
                  side="top"
                  signalIndex={signalIndex}
                  onReorderSignals={handleReorderSignals}
                  isFirstSignal={signalIndex === 0}
                  isLastSignal={signalIndex === signals.length - 1}
                  logicalOperator={logicalOperator}
                />
                <SignalComponentWithCaseWrapper
                  signal={signal}
                  signalIndex={signalIndex}
                  isReadOnly={isReadOnly}
                  signalUnits={signalUnits}
                  onDuplicate={handleDuplicateSignal}
                  onDeleteSignal={handleDeleteSignal}
                  onReorderGroups={handleReorderGroups}
                />
                <BetweenSignalDropBorder
                  side="bottom"
                  signalIndex={signalIndex + 1}
                  onReorderSignals={handleReorderSignals}
                  isFirstSignal={signalIndex === 0}
                  isLastSignal={signalIndex === signals.length - 1}
                  logicalOperator={logicalOperator}
                />
              </ItemAndDropBorderContainer>
            ))}
          </VStack>
        )}
        {signals.length > 0 && (
          <>
            <Flex justifyContent={signals.length > 1 ? 'center' : 'flex-start'}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCreateEmptySignalFromOr}
              >
                Or +
              </Button>
            </Flex>
          </>
        )}
      </VStack>
    </VStack>
  );
};
